<template>
  <v-card-text class="card-content">
    <v-list dense class="extension-list">
      <v-list-item
        v-for="durchwahl in filteredDurchwahlen"
        :key="durchwahl.id"
        class="extension-item"
        rounded
      >
        <div class="d-flex align-center justify-space-between w-100">
          <div class="extension-info">
            <v-list-item-title class="extension-name">
              {{ durchwahl.name }}
              {{ durchwahl.vorname ? durchwahl.vorname : "" }}
            </v-list-item-title>
            <v-list-item-subtitle
              v-if="durchwahl.abteilung"
              class="extension-department"
            >
              {{ durchwahl.abteilung }}
            </v-list-item-subtitle>
          </div>
          <div class="extension-number">
            <v-chip
              size="small"
              class="extension-chip"
              variant="outlined"
              color="success"
              title="Durchwahl kopieren"
              @click="copyToClipboard(durchwahl.durchwahl)"
              href="tel:{{ durchwahl.durchwahl }}"
            >
              <v-icon size="x-small" class="mr-1">mdi-phone</v-icon>
              {{ durchwahl.durchwahl }}
            </v-chip>
          </div>
        </div>
      </v-list-item>

      <v-list-item v-if="filteredDurchwahlen.length === 0" disabled>
        <v-list-item-title class="text-center text-grey">
          Keine Durchwahlen gefunden
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <div class="d-flex justify-end mt-2" v-if="showMoreButton">
      <v-btn
        text
        size="small"
        variant="text"
        color="success"
        class="show-more-button"
        @click="toggleShowAllDurchwahlen"
      >
        <v-icon size="small" class="mr-1">
          {{ showAllDurchwahlen ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
        {{ showAllDurchwahlen ? "Weniger anzeigen" : "Alle anzeigen" }}
      </v-btn>
    </div>
  </v-card-text>
</template>

<script setup>
import { inject, computed } from "vue";
import { useToast } from "vue-toastification";

 
// Props hinzufügen, um Filterung zu unterstützen
const props = defineProps({
  filterQuery: {
    type: String,
    default: ''
  }
});
 

// Inject benötigte Daten und Funktionen
const globalDurchwahlen = inject("globalDurchwahlen");
const displayedDurchwahlen = inject("displayedDurchwahlen");
const showAllDurchwahlen = inject("showAllDurchwahlen");
const toggleShowAllDurchwahlen = inject("toggleShowAllDurchwahlen");

// Gefilterte Durchwahlen basierend auf Props und injizierten Daten
const filteredDurchwahlen = computed(() => {
  // Wenn filterQuery aus Props vorhanden ist, diese verwenden
  if (props.filterQuery) {
    const query = props.filterQuery.toLowerCase();
    return globalDurchwahlen.value.filter(
      item => item.name.toLowerCase().includes(query) ||
              item.vorname?.toLowerCase().includes(query) ||
              item.durchwahl.toLowerCase().includes(query) ||
              item.abteilung?.toLowerCase().includes(query)
    );
  }
  // Sonst die injizierten displayedDurchwahlen verwenden
  return displayedDurchwahlen.value;
});

// Nur "Mehr anzeigen"-Button zeigen, wenn sinnvoll
const showMoreButton = computed(() => {
  // Wenn explizit gefiltert wird, keinen Button zeigen
  if (props.filterQuery) return false;

  // Nur zeigen wenn es mehr Durchwahlen gibt als standardmäßig angezeigt werden
  return globalDurchwahlen.value.length > 4;
});

// Hole die copyToClipboard-Funktion aus dem Parent oder erstelle sie hier
const providedCopyToClipboard = inject("copyToClipboard", null);
const toast = useToast();

// Fallback, wenn die Funktion nicht injiziert werden konnte
const copyToClipboard =
  providedCopyToClipboard ||
  ((text) => {
    try {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success(`Text in die Zwischenablage kopiert!`);
        })
        .catch((err) => {
          console.error("Fehler beim Kopieren in die Zwischenablage:", err);
          toast.error("Kopieren fehlgeschlagen");
        });
    } catch (err) {
      console.error("Fehler beim Kopieren in die Zwischenablage:", err);
      toast.error("Kopieren fehlgeschlagen");
    }
  });
</script>

<style scoped>
.card-content {
  padding: 16px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stats-header {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.2),
    rgba(76, 175, 80, 0.1),
    transparent
  );
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 16px !important;
}

.extension-list {
  max-height: 350px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 4px;
  border: 0px solid rgba(255, 255, 255, 0.05);
}

.extension-item {
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.extension-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.extension-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.extension-department {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.extension-number {
  margin-left: auto;
}

.extension-chip {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.extension-chip:hover {
  background-color: rgba(76, 175, 80, 0.1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.show-more-button .v-icon {
  transition: transform 0.3s ease;
}

.show-more-button:hover .v-icon {
  transform: translateY(-2px);
}
</style>
